import type { ReactNode } from 'react';
import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { ChevronUpIc, ChevronDownIc } from '@dsch/dd-icons';

import { media } from 'helpers/breakpoints';

interface SeeMoreFadeProps {
  mobileContainerMaxHeight: number;
  desktopContainerMaxHeight?: number;
  isContentOverflowing?: boolean;
  children: ReactNode;
  seeMoreText?: string;
  seeLessText?: string;
  hasChevron?: boolean;
}
type ContainerStyleProps = {
  isTextExpanded: boolean;
  mobileContainerMaxHeight: number;
  desktopContainerMaxHeight?: number;
};
const Container = styled.div<ContainerStyleProps>`
  ${({ theme }) => theme.fontSize.M14};
  position: relative;
  overflow: auto;
  overflow-y: hidden;
  height: auto;

  ${({ isTextExpanded, mobileContainerMaxHeight }) =>
    isTextExpanded
      ? `
    ''
  `
      : `max-height: ${mobileContainerMaxHeight}px;`}

  ${({ isTextExpanded, desktopContainerMaxHeight }) =>
    desktopContainerMaxHeight &&
    (isTextExpanded
      ? `
        ''
      `
      : `${media.large} {max-height: ${desktopContainerMaxHeight}px;}`)}
`;
type SeeMoreFadeUnitStyleProps = {
  isTextExpanded: boolean;
};
const SeeMoreFadeUnit = styled.button<SeeMoreFadeUnitStyleProps>`
  width: 100%;
  display: flex;
  justify-content: center;
  cursor: pointer;

  ${({ isTextExpanded }) =>
    isTextExpanded
      ? `position: relative`
      : `background: linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 0.01%, #FFFFFF 90%); 
        padding-top: 72px;
        position: absolute;
        bottom: 0;`}
`;

const SeeMoreWrapper = styled.div`
  display: flex;
  align-items: center;
`;

type TSeeMoreText = { hasChevron: boolean };
const SeeMoreText = styled.p<TSeeMoreText>`
  ${({ theme }) => theme.fontSize.M14};
  color: ${({ theme }) => theme.colors.BLUE};
  margin-right: ${({ theme, hasChevron }) =>
    hasChevron ? theme.spacing.S4 : '0'};
`;

function SeeMoreFade({
  children,
  mobileContainerMaxHeight,
  desktopContainerMaxHeight,
  isContentOverflowing = true,
  seeMoreText = 'See More',
  seeLessText = 'See Less',
  hasChevron = false,
}: SeeMoreFadeProps) {
  const theme = useTheme();
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  function handleTextExpansion() {
    setIsTextExpanded(!isTextExpanded);
  }

  return (
    <Container
      isTextExpanded={isTextExpanded}
      mobileContainerMaxHeight={mobileContainerMaxHeight}
      desktopContainerMaxHeight={desktopContainerMaxHeight}
    >
      {children}
      {isContentOverflowing && (
        <SeeMoreFadeUnit
          data-testid="see-more-element"
          isTextExpanded={isTextExpanded}
          onClick={handleTextExpansion}
        >
          <SeeMoreWrapper data-testid="see-more-text">
            <SeeMoreText hasChevron={hasChevron}>
              {isTextExpanded ? seeLessText : seeMoreText}
            </SeeMoreText>
            {hasChevron && (
              <>
                {isTextExpanded ? (
                  <ChevronUpIc
                    width={16}
                    height={16}
                    color={theme.colors.BLUE}
                  />
                ) : (
                  <ChevronDownIc
                    width={16}
                    height={16}
                    color={theme.colors.BLUE}
                  />
                )}
              </>
            )}
          </SeeMoreWrapper>
        </SeeMoreFadeUnit>
      )}
    </Container>
  );
}

export { SeeMoreFade };
